import axios from "axios";
import { useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import { GrUpdate } from "react-icons/gr";

export default function useCheckAppVersion(intervalMs = 60000) {
	const currentVersion = useRef(null);
	const hasNotified = useRef(false);
	const intervalRef = useRef(null);

	// Helpers para localStorage
	const getStoredVersion = () => localStorage.getItem("app_version");
	const setStoredVersion = (version) =>
		localStorage.setItem("app_version", version);
	const clearStoredVersion = () => localStorage.removeItem("app_version");

	const showUpdateToast = useCallback(() => {
		hasNotified.current = true;
		toast.info(
			<div className="flex items-center gap-3">
				<span>Hay una nueva versión disponible.</span>
				<button
					onClick={() => {
						clearStoredVersion();
						toast.dismiss();
						toast.info(
							<div className="flex items-center gap-2">
								<div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
								<span>Actualizando...</span>
							</div>,
							{ autoClose: false }
						);
						setTimeout(() => window.location.reload(), 1000);
					}}
					className="bg-blue-500 hover:bg-blue-600 text-white text-xs px-4 py-2 rounded-md transition shadow-sm"
				>
					<GrUpdate size={15} />
				</button>
			</div>,
			{ autoClose: false }
		);
	}, []);

	const checkVersion = useCallback(async () => {
		try {
			const { data } = await axios.get("/version.json", {
				headers: { "Cache-Control": "no-store" },
			});

			const newVersion = data.version;
			if (!currentVersion.current) {
				currentVersion.current = newVersion;
				setStoredVersion(newVersion);
				return;
			}

			if (currentVersion.current !== newVersion && !hasNotified.current) {
				showUpdateToast();
			} else {
				currentVersion.current = newVersion;
				setStoredVersion(newVersion);
			}
		} catch (error) {
			console.error("❌ Error al verificar la versión de la app:", error);
		}
	}, [showUpdateToast]);

	// visibilidad de pestaña
	const handleVisibilityChange = useCallback(() => {
		if (document.hidden) {
			// Pausar chequeos
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		} else {
			// Reanudar chequeos
			if (!intervalRef.current) {
				intervalRef.current = setInterval(checkVersion, intervalMs);
			}
		}
	}, [checkVersion, intervalMs]);

	useEffect(() => {
		currentVersion.current = getStoredVersion();
		checkVersion();

		// Iniciar intervalo
		intervalRef.current = setInterval(checkVersion, intervalMs);

		// Escuchar cambios de visibilidad
		document.addEventListener("visibilitychange", handleVisibilityChange);

		return () => {
			clearInterval(intervalRef.current);
			document.removeEventListener("visibilitychange", handleVisibilityChange);
		};
	}, [checkVersion, intervalMs, handleVisibilityChange]);
}
